// hooks/useComments.js
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  addDoc,
  getDocs,
  where,
  documentId,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@root/firebase';
import { toast } from 'react-hot-toast';

// Firestore "in" queries accept up to 10 ids
const chunk = (arr, size = 10) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );

function useComments(
  parentId,
  collectionPath,
  globalUser,
  defaultAvatarUrl,
  parentIdNull = false
) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);

  const commentsColRef = useMemo(() => {
    if (!parentId || !collectionPath) return null;
    return collection(db, `${collectionPath}/${parentId}/comments`);
  }, [collectionPath, parentId]);

  const hydrateWithUsers = useCallback(
    async (raw) => {
      const ids = Array.from(new Set(raw.map((c) => c.userId).filter(Boolean)));
      if (ids.length === 0) {
        return raw.map((c) => ({
          ...c,
          user: {
            id: c.userId || 'unknown',
            name: 'Unknown User',
            avatar: defaultAvatarUrl,
          },
        }));
      }

      const usersMap = {};
      // Batch in chunks of 10
      for (const idsChunk of chunk(ids, 10)) {
        const q = query(
          collection(db, 'users'),
          where(documentId(), 'in', idsChunk)
        );
        const snap = await getDocs(q);
        snap.forEach((u) => {
          const d = u.data() || {};
          usersMap[u.id] = {
            id: u.id,
            name:
              d.displayName ||
              d.name ||
              [d.firstName, d.lastName].filter(Boolean).join(' ') ||
              'Unknown User',
            avatar:
              d.photoURL || d.avatar || d.profileImage || defaultAvatarUrl,
          };
        });
      }

      return raw.map((c) => ({
        ...c,
        user: usersMap[c.userId] || {
          id: c.userId || 'unknown',
          name: 'Unknown User',
          avatar: defaultAvatarUrl,
        },
      }));
    },
    [defaultAvatarUrl]
  );

  useEffect(() => {
    if (!commentsColRef) return;
    setLoading(true);

    const q = query(commentsColRef, orderBy('createdAt', 'desc'));

    const unsub = onSnapshot(
      q,
      async (snapshot) => {
        const raw = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        try {
          const joined = await hydrateWithUsers(raw);
          setComments(joined);
        } catch (e) {
          console.error('Failed to join users for comments:', e);
          // Fallback to raw if user join fails
          setComments(
            raw.map((c) => ({
              ...c,
              user: {
                id: c.userId || 'unknown',
                name: 'Unknown User',
                avatar: defaultAvatarUrl,
              },
            }))
          );
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error('Comments snapshot error:', error);
        setComments([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [commentsColRef, hydrateWithUsers]);

  const addComment = useCallback(
    async (commentText) => {
      if (!commentText?.trim()) return;
      if (!commentsColRef) return;

      try {
        const commentData = {
          text: commentText.trim(),
          createdAt: serverTimestamp(),
          userId: globalUser?.uid || null,
          ...(parentIdNull ? { parentId: null } : {}),
        };
        await addDoc(commentsColRef, commentData);
      } catch (error) {
        console.error('Error adding comment:', error);
        toast.error('Failed to add comment');
      }
    },
    [commentsColRef, globalUser?.uid, parentIdNull]
  );

  return { comments, loading, addComment };
}

export default useComments;
